import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaStar, FaQuoteLeft, FaQuoteRight } from 'react-icons/fa';
import { apiService } from '../services/api';
import SEO from '../components/SEO';

const TestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService.testimonials.getAll()
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setTestimonials(data);
        setLoading(false);
      })
      .catch(() => {
        // Mock data fallback
        setTestimonials([
          { 
            id: 1, 
            name: 'Rajesh Kumar', 
            role: 'Parent', 
            testimonial: 'Vidya Vatika mein padhai ke sath-sath bachchon ka overall development hota hai. Teachers bahut supportive hain.',
            rating: 5,
            className: 'Class 10 Parent'
          },
          { 
            id: 2, 
            name: 'Priya Sharma', 
            role: 'Student', 
            testimonial: 'Mujhe yahan ki science labs aur computer facilities bahut pasand hain. School ka environment positive hai.',
            rating: 5,
            className: 'Class 9'
          },
          { 
            id: 3, 
            name: 'Amit Verma', 
            role: 'Parent', 
            testimonial: 'School ki discipline aur values education ne mere bachche ko confident banaya hai. Highly recommended!',
            rating: 5,
            className: 'Class 7 Parent'
          },
          { 
            id: 4, 
            name: 'Sneha Gupta', 
            role: 'Alumni', 
            testimonial: 'Vidya Vatika ne mujhe life ke liye taiyar kiya. Yahan seekhe gaye values aaj bhi kaam aate hain.',
            rating: 5,
            className: 'Batch of 2020'
          }
        ]);
        setLoading(false);
      });
  }, []);

  return (
    <>
      <SEO 
        title="Parent & Student Reviews | Vidya Vatika World School Panipat"
        description="Read what students and parents say about their experience at Vidya Vatika World School. Real testimonials from our community."
        keywords="school reviews Panipat, parent testimonials, student feedback, best school ratings Haryana, success stories, alumni reviews"
        canonicalUrl="/testimonials"
        ogImage="/images/og-image.jpg"
      />
      
      <div className="pt-20 min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-900 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">Testimonials</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            What Our Students & Parents Say About Us
          </p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="spinner w-16 h-16 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading testimonials...</p>
            </div>
          ) : testimonials.length === 0 ? (
            <p className="text-center text-gray-600 py-12 max-w-lg mx-auto">
              No approved testimonials yet. After admin adds or approves entries, they will appear here.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all"
                >
                  {/* Rating Stars */}
                  <div className="flex mb-4">
                    {[...Array(Math.min(5, Math.max(1, Number(testimonial.rating) || 5)))].map((_, i) => (
                      <FaStar key={i} className="text-yellow-400" />
                    ))}
                  </div>

                  {/* Quote Icon */}
                  <FaQuoteLeft className="text-4xl text-blue-200 mb-4" />

                  {/* Testimonial Text */}
                  <p className="text-gray-700 italic leading-relaxed mb-6">
                    "{testimonial.testimonial}"
                  </p>

                  <FaQuoteRight className="text-4xl text-blue-200 mb-4 ml-auto" />

                  {/* Author Info */}
                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-bold text-blue-900">{testimonial.name}</h3>
                    <p className="text-sm text-gray-600">
                      {testimonial.role} {testimonial.className && `• ${testimonial.className}`}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-purple-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">
            Join Our Vidya Vatika Family
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Experience excellence in education with holistic development
          </p>
          <a
            href="/admissions"
            className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-semibold transition-all text-lg shadow-lg hover:shadow-xl"
          >
            Admissions Open - Enroll Now!
          </a>
        </div>
      </section>
    </div>
    </>
  );
};

export default TestimonialsPage;
